import { Trans, useLingui } from "@lingui/react/macro";
import type { Me } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authClient } from "../../lib/auth";
import { rpc, selectSpace } from "../../lib/rpc";
import type { TeamTemplate } from "../../lib/team-templates";
import { CreateTeamDialog } from "./create-team-dialog";

/** An account creates its company before connecting a model for its members. */
export function CompanyWelcome({
  me,
  onCreated,
}: {
  me: Me;
  onCreated: (spaceId: string) => void;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create(template: TeamTemplate, name: string, idempotencyKey: string) {
    setError(null);
    try {
      const { spaceId, groupId } = await rpc.companies.create({
        name,
        platform: template.id,
        idempotencyKey,
      });
      if (!selectSpace(spaceId)) throw new Error(t`Could not switch spaces`);
      onCreated(spaceId);
      navigate(me.needsModel ? "/onboarding/model" : `/app/g/${groupId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t`Could not create company`);
      throw err;
    }
  }

  return (
    <div className="flex min-h-full flex-col bg-background">
      <header className="flex items-center justify-between px-6 py-4">
        <span className="text-sm font-medium text-foreground">ClVaBot</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void authClient.signOut().then(() => navigate("/", { replace: true }))}
        >
          <Trans>Log out</Trans>
        </Button>
      </header>
      <main className="flex flex-1 flex-col items-center justify-center gap-4 px-6 pb-24">
        <h1 className="text-heading3 font-semibold text-foreground">
          <Trans>Welcome to ClVaBot</Trans>
        </h1>
        <Button onClick={() => setCreating(true)}>
          <Trans>New company</Trans>
        </Button>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </main>
      {creating ? <CreateTeamDialog onCancel={() => setCreating(false)} onCreate={create} /> : null}
    </div>
  );
}
